import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { INITIAL_LOADER_STATES } from '@app-live-view/constants/live-view.constants';
import { LIVESTREAM_DEFAULT_PIP_MAIN_FRAME, LIVESTREAM_VIDEO_RESOLUTIONS, VIDEO_FORMATS } from '@app-core/constants/constants';
import { dirtyCheck, DirtyComponent } from '@app-core/models/dirty-check';
import { RequestWebRTCLivestreamResp, DeviceUsageResponse } from '@app-core/models/livestream';
import { AccessService } from '@app-core/services/access/access.service';
import { LivestreamService } from '@app-core/services/livestream/livestream.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, of, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TimerState } from '@app-shared/components/timer/timer.component';
import { DataService } from '@app-core/services/data/data.service';
import { MDVR_AVAILBLE_VIEWS } from '@app-assets/constants/assets.constants';
import { MatSelectChange } from '@angular/material/select';
import { MDVR_COLLAGE_CONFIG, MDVR_COLLAGE_SORTING_ARR } from '@app-request-video/constants/request-video.constants';
import { PreferencesConfig } from '@app-core/models/core.model';
import { Socket } from 'socket.io-client';
import { LiveTelematicsService } from '@app-core/services/live-telematics/live-telematics.service';
import { LivestreamStatusUpdateMessage, LivestreamStatus } from '@app-live-view/models/live-view.model';
import { LivestreamTimerComponent } from '@app-shared/components/livestream-timer/livestream-timer.component';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { LiveStreamSdkService } from '@app-live-view/services/webrtc-sdk/live-stream-sdk.service';
import { STREAM_MODES, UNIT_SYSTEMS, VIDEO_TYPES, VideoType, StreamSessionConfig } from '@rideview/livestream-web-sdk';

interface LivestreamModalData {
  assetId?: string;
  deviceId?: string;
  deviceModel?: string;
  tripId: string;
  asset: any;
  recordedInfo: any[];
  socketConnection: Socket;
  enableDriverCamera?: boolean;
}

interface LiveStreamRequestFg {
  videoResolution: FormControl<string>;
  videoFormat: FormControl<string>;
  pipVideoFormat: FormControl<string>;
  mdvrViews?: FormControl<string | string[]>;
}

const RESOLUTION_1080 = '1920x1080';
const RESOLUTION_720 = '1280x720';
const RESOLUTION_360 = '640x360';
// Devices to disable for side by side 720p view
// https://lightmetrics.atlassian.net/browse/LF-2991?atlOrigin=eyJpIjoiNjUzZDhiMzdjNGE5NDFjOTg5OGQ1M2Q5OTQ2MTlmZDUiLCJwIjoiaiJ9
const DEVICE_MODELS_WITHOUT_720_RESOLUTION = ['jimi-jc261', 'jimi-jc261p', 'jimi-jc450', 'jimi-jc400', 'jimi-jc400p', 'stsunlab-st9730'];

// todo - name every file related to webrtc streaming as - WebRTCLiveStream
@Component({
  selector: 'app-livestream-modal-webrtc',
  templateUrl: './webrtc-livestream-modal.component.html',
  styleUrls: ['./webrtc-livestream-modal.component.scss'],
})
export class WebRTCLivestreamModalComponent implements OnInit, OnDestroy, DirtyComponent {
  @ViewChild('livestreamTimer', { static: true })
  private livestreamTimer: LivestreamTimerComponent;

  @ViewChild('videoPlayer', { static: true }) videoPlayer: any;

  public isDirty$ = of(false);
  public livestreamResolutions: string[] = LIVESTREAM_VIDEO_RESOLUTIONS;
  public livestreamVideoFormats = VIDEO_FORMATS.filter((v) => v.value !== 'separate');
  public livestreamForm: FormGroup;
  public livestreamDetails: RequestWebRTCLivestreamResp;
  public conflictStreamId: string;
  public errorMessage: string;
  public isRequestingLivestream = false;
  public sessionExpired = false;
  public verifySessionInterval = 2 * 60 + 45;
  public verifySession = false;
  public showReviewActionDialog = false;
  public enabledRetryOnFailure = true;
  public fleetId: string;
  public noActionTimerState: TimerState = {
    count: true,
    countup: false,
    value: 15,
    speed: 1000,
    increase: 1,
    pauseAt: 0,
  };
  public availableMdvrViews = [];
  public isEvoCamera: boolean;
  public deviceUsageLoader: boolean = true;
  public deviceUsage: number = 0;
  public baseMonthlySecondsLoader = true;
  public baseMonthlySeconds = 0;
  public infoMessage: string;

  private ngUnsubscribe = new Subject<void>();
  private unsubscribeLivestreamReq = new Subject<void>();
  private ngUnsubscribeIsDirty = new Subject<void>();
  private preferences: PreferencesConfig;
  public liveStreamError = false;
  public loaderStates = INITIAL_LOADER_STATES();
  public currentMonthLivestreamExtraMinutes = 0;
  public totalMinutes: number = 0;
  resetLoaderStates() {
    this.loaderStates = INITIAL_LOADER_STATES();
  }

  constructor(
    public dialogRef: MatDialogRef<WebRTCLivestreamModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LivestreamModalData,
    private fb: FormBuilder,
    private livestreamService: LivestreamService,
    private snackBarService: SnackBarService,
    public translate: TranslateService,
    private accessService: AccessService,
    private dataService: DataService,
    private liveTelamaticsService: LiveTelematicsService,
    private mixpanelService: MixPanelService,
    private liveStreamSdkService: LiveStreamSdkService
  ) {}

  preventVideoPause() {
    if (this.videoPlayer.nativeElement.paused) {
      this.videoPlayer.nativeElement.play();
    }
  }

  private restartVideoTimeline() {
    const el = this.videoPlayer?.nativeElement;
    if (!el?.srcObject) {
      return;
    }
    const stream = el.srcObject;
    el.srcObject = null;
    el.srcObject = stream;
    el.play().catch(() => {});
  }

  public ngOnInit() {
    this.listenForLivestreamStatusUpdate();
    this.preferences = this.dataService.fleetPreferences?.livestreaming;
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
      }
    });
    this.livestreamResolutions = this.dataService.getResolutionsBetween(
      LIVESTREAM_VIDEO_RESOLUTIONS,
      this.preferences.maxAllowedResolution
    ) as string[];
    this.livestreamVideoFormats = VIDEO_FORMATS.filter((v) => {
      if (
        (v.value === 'pictureInPictureRoadSide' || v.value === 'pictureInPictureDriverSide') &&
        this.preferences.formatOptions.includes('pictureInPicture')
      ) {
        return true;
      } else {
        return this.preferences.formatOptions.includes(v.value);
      }
    });
    this.livestreamForm = this.fb.group<LiveStreamRequestFg>({
      videoResolution: this.fb.control(this.preferences.defaultResolution, [Validators.required]),
      videoFormat: this.fb.control(this.livestreamVideoFormats[0].value, [Validators.required]),
      pipVideoFormat: this.fb.control({ value: LIVESTREAM_DEFAULT_PIP_MAIN_FRAME, disabled: true }),
    });
    this.subscribeForVideoFormatChange();
    this.refreshIsDirty();
    this.setupSdkEventHandlers();
    this.requestLivestream();
    this.getDeviceUsage();
    this.getDeviceBaseMonthlySeconds();
    this.checkAssetForMdvrSupport();
    this.applyDriverCameraFilter();
    this.add1080pResolutionForRoadFacing(this.livestreamForm.get('videoFormat')?.value);
  }

  // 1080p livestream is supported only for the road facing camera
  private add1080pResolutionForRoadFacing(videoFormat: string) {
    if (videoFormat === 'road' && !this.livestreamResolutions.includes(RESOLUTION_1080)) {
      this.livestreamResolutions = [...this.livestreamResolutions, RESOLUTION_1080];
    }
  }

  private applyDriverCameraFilter() {
    if (this.data.enableDriverCamera === false) {
      const driverCameraFormats = ['driver', 'sideBySide', 'pictureInPictureRoadSide', 'pictureInPictureDriverSide', 'separate'];
      this.livestreamVideoFormats = this.livestreamVideoFormats.filter((v) => !driverCameraFormats.includes(v.value));
      const currentFormat = this.livestreamForm.get('videoFormat')?.value;
      if (driverCameraFormats.includes(currentFormat)) {
        this.livestreamForm.patchValue({ videoFormat: this.livestreamVideoFormats[0]?.value });
      }
    }
  }

  private setupSdkEventHandlers() {
    this.liveStreamSdkService.connectionState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((state) => {
      switch (state) {
        case 'connected':
          this.loaderStates.connecting.inProgress = true;
          this.loaderStates.current = 0;
          break;
        case 'playing':
          this.loaderStates.generating.inProgress = false;
          this.loaderStates.current = 3;
          this.restartVideoTimeline();
          this.isRequestingLivestream = false;
          this.livestreamTimer.start();
          break;
        case 'device-connected':
          this.pushLoaderToGeneratingStage();
          break;
        default:
          break;
      }
    });
    this.liveStreamSdkService.error$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.errorMessage = 'Streaming error';
    });
    this.liveStreamSdkService.sessionConflict$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((conflict) => {
      this.conflictStreamId = conflict.conflictingRequestId;
      this.livestreamForm.disable();
      this.unsubscribeFromValueChanges();
      this.showReviewActionDialog = true;
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  public ngOnDestroy() {
    this.stopLivestream();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.unsubscribeLivestreamReq.next();
    this.unsubscribeLivestreamReq.complete();
    this.unsubscribeFromValueChanges();
  }

  private listenForLivestreamStatusUpdate() {
    let activeSocket: Socket;
    if (this.liveTelamaticsService.zoomedOutViewSocket.connected) {
      activeSocket = this.liveTelamaticsService.zoomedOutViewSocket;
    } else {
      activeSocket = this.liveTelamaticsService.deviceLevelViewSocket;
    }

    if (!activeSocket) {
      return;
    }

    fromEvent<LivestreamStatusUpdateMessage>(activeSocket, 'device-live-stream-update')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((lsStatus) => {
        console.log('*** LS Status Update [LS Modal] ***', lsStatus);
        if (this.livestreamDetails?.streamRequestId !== lsStatus.streamRequestId) {
          return;
        }

        if (lsStatus.status === LivestreamStatus.Started) {
          this.infoMessage = 'Livestreaming has started.';
          this.errorMessage = '';
          this.livestreamTimer.start();
        } else {
          this.livestreamTimer.pause();
        }

        if (lsStatus.status === LivestreamStatus.Canceled) {
          this.infoMessage = '';
          this.errorMessage = 'Livestreaming has been canceled.';
          this.enabledRetryOnFailure = false; // Decide whether to show retry button or not
        } else if (lsStatus.status === LivestreamStatus.Unavailable) {
          this.errorMessage = 'Livestreaming is currently unavailable.';
          this.infoMessage = '';
        } else if (lsStatus.status === LivestreamStatus.Finished) {
          this.infoMessage = 'Livestreaming has ended.';
          this.errorMessage = '';
        }
      });
  }

  private unsubscribeFromValueChanges() {
    this.ngUnsubscribeIsDirty.next();
    this.ngUnsubscribeIsDirty.complete();
  }

  private refreshIsDirty() {
    const sourceFormValue = this.livestreamForm.getRawValue();
    // Unsubscribe from previously subscribed valueChanges
    this.ngUnsubscribeIsDirty.next();
    this.isDirty$ = this.livestreamForm.valueChanges.pipe(takeUntil(this.ngUnsubscribeIsDirty), dirtyCheck(of(sourceFormValue)));
  }

  private subscribeForVideoFormatChange() {
    const videoFormatControl = this.livestreamForm.get('videoFormat');
    videoFormatControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((videoFormat) => {
      if (videoFormat === 'pictureInPictureRoadSide') {
        this.livestreamForm.patchValue({ pipVideoFormat: 'road' }, { emitEvent: false });
      } else if (videoFormat === 'pictureInPictureDriverSide') {
        this.livestreamForm.patchValue({ pipVideoFormat: 'driver' }, { emitEvent: false });
      }
    });
  }
  private pushLoaderToGeneratingStage() {
    this.loaderStates.connecting.inProgress = false;
    this.loaderStates.current = 1;
    this.loaderStates.connected.inProgress = true;
    setTimeout(() => {
      this.loaderStates.current = 2;
      this.loaderStates.connected.inProgress = false;
      this.loaderStates.generating.inProgress = true;
    }, 1000);
  }
  private resetState() {
    this.resetLoaderStates();
    this.isRequestingLivestream = true;
    this.errorMessage = null;
    this.infoMessage = null;
    this.enabledRetryOnFailure = true;
    this.unsubscribeLivestreamReq.next();
    this.livestreamTimer.pause();
  }

  private async requestLivestream() {
    const { deviceId } = this.data;
    let { videoResolution, videoFormat, mdvrViews } = this.livestreamForm.getRawValue();
    const unitSystem = this.accessService.currentMetricUnit === 'Kilometers' ? UNIT_SYSTEMS.METRIC : UNIT_SYSTEMS.IMPERIAL;

    let collage: string;
    let selectedMdvrSources: string[];

    if (videoFormat === 'pictureInPictureRoadSide') {
      videoFormat = VIDEO_TYPES.PICTURE_IN_PICTURE_ROAD;
    } else if (videoFormat === 'pictureInPictureDriverSide') {
      videoFormat = VIDEO_TYPES.PICTURE_IN_PICTURE_DRIVER;
    } else if (videoFormat === 'MDVR') {
      const mdvrViewsArr: string[] = this.isEvoCamera ? [...mdvrViews] : [mdvrViews as string];
      selectedMdvrSources = mdvrViewsArr.map((view) => this.availableMdvrViews.find((v2) => v2.view === view).source);
      selectedMdvrSources.sort((a, b) => MDVR_COLLAGE_SORTING_ARR.indexOf(a) - MDVR_COLLAGE_SORTING_ARR.indexOf(b));
      const collageConfig = MDVR_COLLAGE_CONFIG.find((c) => c.validSources === selectedMdvrSources.length);
      if (!collageConfig) {
        return;
      }
      collage = collageConfig.collage;
    }

    this.isRequestingLivestream = true;
    this.resetState();
    this.loaderStates.initiated = false;
    try {
      this.livestreamDetails = await this.liveStreamSdkService.startStreaming(
        {
          fleetId: this.fleetId,
          deviceId: deviceId,
          userId: this.accessService.getUserIdOrHash(),
          videoType: videoFormat as VideoType,
          unitSystem: unitSystem,
          ...(collage ? { collage, sources: selectedMdvrSources, resolution: videoResolution } : { videoResolution }),
        } as StreamSessionConfig,
        STREAM_MODES.WEBRTC
      );
      this.liveStreamSdkService.attach(this.videoPlayer.nativeElement);
      this.errorMessage = '';
    } catch (err) {
      this.livestreamDetails = null;
      this.isRequestingLivestream = false;
      this.errorMessage = 'Failed to request livestream. Please try again';
    } finally {
      this.loaderStates.initiated = true;
    }
  }

  private checkAssetForMdvrSupport() {
    const { recordedInfo = [] } = this.data;
    this.isEvoCamera = recordedInfo.some((r) => r.source.startsWith('TVI') || r.source.startsWith('UVC') || r.source.startsWith('TWI'));
    recordedInfo.sort((a, b) => {
      const index1 = MDVR_COLLAGE_SORTING_ARR.findIndex((s) => s === a.source);
      const index2 = MDVR_COLLAGE_SORTING_ARR.findIndex((s) => s === b.source);
      return index1 - index2;
    });
    this.availableMdvrViews = recordedInfo.map((r) => {
      // TODO: remove || after sdk starts sending view in recordedInfo
      const view = r.view || r.source;
      const mdvrView = {
        source: r.source,
        view,
        viewLabel: this.getMdvrViewLabel(view),
      };
      return mdvrView;
    });

    if (this.isEvoCamera) {
      const videoFormat = this.dataService.getDvrVideoFormat(VIDEO_FORMATS, this.preferences);
      this.livestreamVideoFormats = [
        ...videoFormat.filter((v) => v.value !== 'separate'),
        {
          value: 'MDVR',
          text: 'Multi-View',
        },
      ];
    }
  }

  private async stopLivestream() {
    try {
      this.livestreamDetails = null;
      this.snackBarService.success(this.translate.instant('liveStreamModalStoppingLiveStream'));
      await this.liveStreamSdkService.stopStreaming();
      this.snackBarService.success(this.translate.instant('liveStreamModalStoppedLiveStream'));
    } catch (err) {
      this.errorMessage = 'Failed to stop livestream. Please try again';
    }
  }

  private async reviewLivestream() {
    this.isRequestingLivestream = true;
    this.loaderStates.initiated = false;
    this.resetState();
    this.showReviewActionDialog = false;
    try {
      this.livestreamDetails = await this.liveStreamSdkService.joinLivestream(this.conflictStreamId, this.fleetId, STREAM_MODES.WEBRTC);
      this.liveStreamSdkService.attach(this.videoPlayer.nativeElement);
      this.errorMessage = '';
    } catch (err) {
      this.isRequestingLivestream = false;
      this.errorMessage = 'Failed to request livestream. Please try again';
    } finally {
      this.loaderStates.initiated = true;
    }
  }

  public async onReRequestLivestreamAction() {
    this.mixpanelService.track('Click apply in live streaming');
    this.refreshIsDirty();
    await this.liveStreamSdkService.stopStreaming();
    await this.requestLivestream();
  }

  public continueStreaming() {
    this.verifySession = false;
  }

  private exitFullScreenAndPictureInPicture() {
    if (document.fullscreenEnabled) {
      document.exitFullscreen();
    }
    if (document.pictureInPictureEnabled) {
      document.exitPictureInPicture();
    }
  }

  public verifySessionTimerAlert() {
    this.exitFullScreenAndPictureInPicture();
    this.verifySession = true;
  }

  public onNoActionTimerPause() {
    this.onCloseAction();
  }

  public async onRetryLivestreamAction() {
    await this.liveStreamSdkService.stopStreaming();
    if (this.conflictStreamId) {
      await this.reviewLivestream();
    } else {
      await this.requestLivestream();
    }
  }

  public onCloseAction() {
    this.dialogRef.close();
  }

  // not sure if review functionality will be there or not, so removing this right now
  public onReviewLivestream() {
    const liveStreamDetailsParams = { streamRequestId: this.conflictStreamId };
    this.reviewLivestream();

    this.livestreamService
      .getLivestreamDetails(liveStreamDetailsParams)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((streamDetails) => {
        const { videoType, videoTypeMainFrame } = streamDetails;
        let videoFormat = videoType;
        if (videoType === 'pictureInPicture') {
          videoFormat = videoTypeMainFrame === 'road' ? 'pictureInPictureRoadSide' : 'pictureInPictureDriverSide';
        }

        this.livestreamForm.patchValue({
          videoResolution: streamDetails.videoResolution,
          videoFormat,
          pipVideoFormat: streamDetails.videoTypeMainFrame,
        });
      });
  }

  public onVideoFormatChange(event: MatSelectChange) {
    const videoFormat = event.value;

    this.livestreamResolutions = this.dataService.getResolutionsBetween(
      LIVESTREAM_VIDEO_RESOLUTIONS,
      this.preferences.maxAllowedResolution
    ) as string[];

    this.add1080pResolutionForRoadFacing(videoFormat);

    const videoResolutionCtrl = this.livestreamForm.get('videoResolution');

    if (videoFormat !== 'road' && videoResolutionCtrl?.value === RESOLUTION_1080) {
      videoResolutionCtrl.patchValue(RESOLUTION_720, { emitEvent: false, onlySelf: false });
    }

    if (videoFormat === 'sideBySide') {
      const hasAssetsWithout720Resolution = DEVICE_MODELS_WITHOUT_720_RESOLUTION.includes(this.data.deviceModel);

      this.livestreamResolutions = this.livestreamResolutions.filter(
        (res) => res !== RESOLUTION_1080 && !(res === RESOLUTION_720 && hasAssetsWithout720Resolution)
      );

      if (videoResolutionCtrl?.value === RESOLUTION_1080) {
        videoResolutionCtrl.patchValue(RESOLUTION_720, { emitEvent: false, onlySelf: false });
      }

      if (videoResolutionCtrl?.value === RESOLUTION_720 && hasAssetsWithout720Resolution) {
        videoResolutionCtrl.patchValue(RESOLUTION_360, { emitEvent: false, onlySelf: false });
      }
    } else if (videoFormat === 'MDVR' && this.isEvoCamera) {
      this.livestreamForm.addControl('mdvrViews', this.fb.control('', [Validators.required]));
      videoResolutionCtrl.reset();
      videoResolutionCtrl.disable();
    } else {
      videoResolutionCtrl.enable();
      this.livestreamForm.removeControl('mdvrViews');
    }
  }

  public onMdvrViewsSelection(event: MatSelectChange) {
    const selectedViews = event.value;
    const videoResolutionCtrl = this.livestreamForm.get('videoResolution');
    if (this.isEvoCamera) {
      videoResolutionCtrl.reset();
      if (selectedViews.length) {
        const collageConfig = MDVR_COLLAGE_CONFIG.find((c) => c.validSources === selectedViews.length);
        if (!collageConfig) {
          videoResolutionCtrl.disable();
          return;
        }
        videoResolutionCtrl.enable();
        this.livestreamResolutions = collageConfig.liveStreamResolution || collageConfig.resolution;
      } else {
        videoResolutionCtrl.disable();
      }
    }
  }

  public getMdvrViewLabel(view: string) {
    if (!view) {
      return '';
    }
    if (view === 'ROAD') {
      return 'Road';
    } else if (view === 'DRIVER') {
      return 'Driver';
    } else {
      return MDVR_AVAILBLE_VIEWS.find((v) => v.value === view)?.label || view;
    }
  }

  public getDeviceUsage() {
    this.deviceUsageLoader = true;
    const { deviceId } = this.data;
    this.livestreamService
      .getDeviceUsage({ deviceId: deviceId })
      .pipe(
        finalize(() => {
          this.deviceUsageLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: DeviceUsageResponse) => {
          const { totalDuration = 0 } = res || {};
          this.deviceUsage = Math.floor(totalDuration / 60);
        },
        () => {
          this.deviceUsage = 0;
        }
      );
  }

  private getDeviceBaseMonthlySeconds() {
    this.baseMonthlySecondsLoader = true;
    const { assetId } = this.data;
    this.livestreamService
      .getAssetDetails({ assetId, includeLiveStreamData: true })
      .pipe(
        finalize(() => {
          this.baseMonthlySecondsLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any) => {
          const { baseMonthlySeconds = 0, currentMonthLivestreamExtraMinutes = 0 } = res || {};
          this.baseMonthlySeconds = Math.round(baseMonthlySeconds / 60);
          this.currentMonthLivestreamExtraMinutes = currentMonthLivestreamExtraMinutes;
          this.totalMinutes = this.baseMonthlySeconds + currentMonthLivestreamExtraMinutes;
        },
        () => {
          this.baseMonthlySeconds = 0;
          this.currentMonthLivestreamExtraMinutes = 0;
          this.totalMinutes = 0;
        }
      );
  }
}
